import { Descriptions, Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import { niiReportConfigSchema } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { NiiPdfExportButton } from 'features/libra/stepComponents/niiReport/pdf/NiiPdfExportButton'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import { useZoneData } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import {
  ELIGIBILITY_COLORS,
  ELIGIBILITY_LABELS,
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { PermissionsGate } from 'features/permissions'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

/** Closeout summary: Gecko internal only. Campaign ROI dashboard + editable report economics also require `libra-executive`. */

export const NiiReportReviewPanel: FC<{
  config: Record<string, unknown>
}> = ({ config }) => {
  const { fields, zoneFieldName } = niiReportConfigSchema.parse(config)
  const stepId = useActiveStepId()
  const { fieldValues, isLoading } = useTicketAccess()
  const { siteName, unit, isLoading: isLoadingAsset } = useLibraAssetModelData()
  const { zones } = useZoneData(zoneFieldName)

  const areaM2 = useMemo(
    () => unwrapFieldValue<number>(fieldValues[fields.inspectableArea]) ?? 0,
    [fieldValues, fields.inspectableArea]
  )

  if (isLoading || isLoadingAsset) {
    return <LoadingText>Loading report data...</LoadingText>
  }

  const eligibility =
    unwrapFieldValue<EligibilityStatus>(fieldValues[fields.eligibility]) ??
    ELIGIBILITY_STATUS.UNCLEAR
  const analysisDate = unwrapFieldValue<string>(
    fieldValues[fields.analysisDate]
  )
  const executiveSummary = unwrapFieldValue<string>(
    fieldValues[fields.executiveSummary]
  )
  const internalComments = unwrapFieldValue<string>(
    fieldValues[fields.internalComments]
  )
  const recommendations =
    unwrapFieldValue<ReadonlyArray<string>>(
      fieldValues[fields.recommendations]
    ) ?? []
  const justification = fields.justification
    ? unwrapFieldValue<string>(fieldValues[fields.justification])
    : undefined

  const zonesWithRecommendations = zones.filter(
    (z) => z.zone_recommendations && z.zone_recommendations.length > 0
  )

  return (
    <Container>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.analysisDate }}>
        <StyledCard title='Basic Information'>
          <Descriptions column={1} bordered size='small'>
            {siteName && (
              <Descriptions.Item label='Site'>{siteName}</Descriptions.Item>
            )}
            {unit?.data.name && (
              <Descriptions.Item label='Asset'>
                {unit.data.name}
              </Descriptions.Item>
            )}
            <Descriptions.Item label='Analysis Date'>
              {analysisDate ?? '—'}
            </Descriptions.Item>
          </Descriptions>
        </StyledCard>
      </CommentIndicator>

      {executiveSummary && (
        <CommentIndicator
          anchor={{ type: 'field', stepId, fieldId: fields.executiveSummary }}>
          <StyledCard title='Executive Summary'>
            <PreformattedText>{executiveSummary}</PreformattedText>
          </StyledCard>
        </CommentIndicator>
      )}

      {internalComments && (
        <CommentIndicator
          anchor={{ type: 'field', stepId, fieldId: fields.internalComments }}>
          <StyledCard title='Internal Assessment Comments'>
            <PreformattedText>{internalComments}</PreformattedText>
          </StyledCard>
        </CommentIndicator>
      )}

      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.eligibility }}>
        <StyledCard title='Conclusion'>
          <Descriptions column={1} bordered size='small'>
            <Descriptions.Item label='Eligibility'>
              <Tag color={ELIGIBILITY_COLORS[eligibility]}>
                {ELIGIBILITY_LABELS[eligibility]}
              </Tag>
            </Descriptions.Item>
            {recommendations.length > 0 && (
              <Descriptions.Item label='Recommendations'>
                <RecommendationsList>
                  {recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </RecommendationsList>
              </Descriptions.Item>
            )}
            {justification && (
              <Descriptions.Item label='Justification'>
                {justification}
              </Descriptions.Item>
            )}
          </Descriptions>
        </StyledCard>
      </CommentIndicator>

      {zonesWithRecommendations.length > 0 && (
        <StyledCard title='Zone-Specific Recommendations'>
          <ZoneRecommendationsContainer>
            {zonesWithRecommendations.map((zone) => (
              <CommentIndicator
                key={zone.id}
                anchor={{
                  type: 'zone-property',
                  stepId,
                  zoneId: zone.id,
                  property: 'zone_recommendations',
                }}>
                <ZoneRecommendationsBlock>
                  <ZoneRecommendationsHeading>
                    <ZoneColorDot color={zone.color ?? '#3B82F6'} />
                    {zone.title}
                  </ZoneRecommendationsHeading>
                  <RecommendationsList>
                    {zone.zone_recommendations!.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </RecommendationsList>
                </ZoneRecommendationsBlock>
              </CommentIndicator>
            ))}
          </ZoneRecommendationsContainer>
        </StyledCard>
      )}

      <PermissionsGate
        requires={[
          PERMISSION_GECKO_INTERNAL_MEMBER,
          PERMISSION_LIBRA_EXECUTIVE,
        ]}>
        <CommentIndicator
          anchor={{
            type: 'field',
            stepId,
            fieldId: fields.inspectableArea,
          }}>
          <StyledCard title='Inspection economics'>
            <EconTable>
              <EconRow>
                <EconLabel>Inspectable area (m²)</EconLabel>
                <EconValue>{areaM2 > 0 ? areaM2 : '—'}</EconValue>
              </EconRow>
            </EconTable>
          </StyledCard>
        </CommentIndicator>
      </PermissionsGate>
      <NiiPdfExportButton fields={fields} zoneFieldName={zoneFieldName} />
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const PreformattedText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
`

const RecommendationsList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;

  li {
    margin-bottom: 0.25rem;
  }
`

const LoadingText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  padding: 0.5rem 0;
`

const EconTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const EconRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  font-size: 0.875rem;
`

const EconLabel = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const EconValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-variant-numeric: tabular-nums;
`

const ZoneRecommendationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ZoneRecommendationsBlock = styled.div`
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const ZoneRecommendationsHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const ZoneColorDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`
